import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import licenseService from '../../services/licenseService';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

const BILLING_CYCLES = ['Monthly', 'Annual', 'Perpetual'];

export default function LicenseProductsAdmin() {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({ vendorId: '', name: '', description: '', defaultMonthlyPrice: '', billingCycle: 'Monthly', maxAssignments: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, product: null });
  const toast = useToast();

  useEffect(() => { loadVendors(); }, []);
  useEffect(() => { loadProducts(); }, [selectedVendorId, showInactive]);

  const loadVendors = async () => {
    try {
      const data = await licenseService.getVendors(true);
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load vendors', e);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await licenseService.getProducts(selectedVendorId || null, showInactive);
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (product = null) => {
    setEditingProduct(product);
    setForm(product ? {
      vendorId: product.vendorId,
      name: product.name,
      description: product.description || '',
      defaultMonthlyPrice: product.defaultMonthlyPrice?.toString() || '',
      billingCycle: product.billingCycle || 'Monthly',
      maxAssignments: product.maxAssignments?.toString() || '',
      isActive: product.isActive
    } : { vendorId: selectedVendorId || '', name: '', description: '', defaultMonthlyPrice: '', billingCycle: 'Monthly', maxAssignments: '', isActive: true });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setForm({ vendorId: '', name: '', description: '', defaultMonthlyPrice: '', billingCycle: 'Monthly', maxAssignments: '', isActive: true });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.vendorId) { toast.error('Vendor is required'); return; }
    
    const payload = {
      vendorId: parseInt(form.vendorId),
      name: form.name.trim(),
      description: form.description.trim() || null,
      defaultMonthlyPrice: form.defaultMonthlyPrice ? parseFloat(form.defaultMonthlyPrice) : null,
      billingCycle: form.billingCycle || null,
      maxAssignments: form.maxAssignments ? parseInt(form.maxAssignments) : null,
      isActive: form.isActive
    };

    setSaving(true);
    try {
      if (editingProduct) {
        await licenseService.updateProduct(editingProduct.id, payload);
        toast.success('Product updated');
      } else {
        await licenseService.createProduct(payload);
        toast.success('Product created');
      }
      closeDialog();
      loadProducts();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.product) return;
    try {
      await licenseService.deleteProduct(deleteConfirm.product.id);
      toast.success('Product deactivated');
      setDeleteConfirm({ open: false, product: null });
      loadProducts();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to delete');
    }
  };

  if (loading && vendors.length === 0) return <SkeletonBlock lines={5} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">License Products</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField select size="small" label="Filter by Vendor" value={selectedVendorId} onChange={(e) => setSelectedVendorId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">All Vendors</MenuItem>
            {vendors.filter(v => v.isActive).map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />} label="Show inactive" />
          <IconButton onClick={loadProducts} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add Product</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vendor</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Price/Month</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell align="center">Assigned/Max</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No products found</TableCell></TableRow>
            ) : products.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.vendorName}</TableCell>
                <TableCell><strong>{p.name}</strong></TableCell>
                <TableCell align="right">{p.defaultMonthlyPrice != null ? `${p.defaultMonthlyPrice.toFixed(2)} лв` : '-'}</TableCell>
                <TableCell>{p.billingCycle || '-'}</TableCell>
                <TableCell align="center">{p.activeAssignmentCount ?? 0}{p.maxAssignments ? `/${p.maxAssignments}` : ''}</TableCell>
                <TableCell><Chip label={p.isActive ? 'Active' : 'Inactive'} color={p.isActive ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openDialog(p)}><EditIcon /></IconButton>
                  {p.isActive && <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, product: p })}><DeleteIcon /></IconButton>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Vendor" value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} required sx={{ mt: 1, mb: 2 }}>
            {vendors.filter(v => v.isActive).map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required sx={{ mb: 2 }} />
          <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField type="number" label="Default Price/Month" value={form.defaultMonthlyPrice} onChange={(e) => setForm({ ...form, defaultMonthlyPrice: e.target.value })} sx={{ flex: 1 }} InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
            <TextField select label="Billing Cycle" value={form.billingCycle} onChange={(e) => setForm({ ...form, billingCycle: e.target.value })} sx={{ flex: 1 }}>
              {BILLING_CYCLES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Box>
          <TextField type="number" fullWidth label="Max Assignments (leave empty for unlimited)" value={form.maxAssignments} onChange={(e) => setForm({ ...form, maxAssignments: e.target.value })} sx={{ mb: 2 }} InputProps={{ inputProps: { min: 1 } }} />
          <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, product: null })}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate <strong>{deleteConfirm.product?.name}</strong>?</Typography>
          {deleteConfirm.product?.activeAssignmentCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>This product has {deleteConfirm.product.activeAssignmentCount} active assignment(s).</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, product: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Deactivate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

